import { CurrencyCode } from 'further-types/lp/vehicle';
import {
  FormProvider,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { carrySettings } from 'further-ui/validations';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Chip } from '@mui/material';
import { Column, Row } from 'further-ui/components/Layout';
import { Typography } from '@mui/material';
import { CarriedInterestFeeTemplate } from 'further-types/lp/carry-settings';
import useCarriedInterestFeeTemplatesOptions from './hooks/useCarriedInterestFeeTemplatesOptions';
import CarriedInterestFeeTemplateComponent from './CarriedInterestFeeTemplate';
import { z } from 'zod';
import api from 'lib/trpcClient';
import useHandleTrpcMutation from 'hooks/ui/useHandleTrpcMutation';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from 'further-ui/components/Accordion';
import StepIndex from 'further-ui/components/StepIndex';
import PageSection from 'components/new/PageSection';
import { useCarrySettingsContext } from '../CarrySettingsContext';

type FormValues = z.infer<
  typeof carrySettings.CarriedInterestFeeTemplatesSettings
>;

type Props = {
  vehicle: {
    _id: string;
    baseCurrency: CurrencyCode;
    carrySettings: {
      carriedInterestFeeTemplates: Array<
        CarriedInterestFeeTemplate & { isInUse: boolean }
      >;
    };
  };
};

const CarriedInterestFeeTemplates: React.FC<Props> = ({ vehicle }) => {
  const formMethods = useForm<FormValues>({
    resolver: zodResolver(carrySettings.CarriedInterestFeeTemplatesSettings),
    shouldFocusError: true,
    values: vehicle.carrySettings,
    criteriaMode: 'all',
  });

  const templateIdsInUse = vehicle.carrySettings.carriedInterestFeeTemplates
    .filter((template) => template.isInUse)
    .map((template) => template._id);

  const { control, handleSubmit } = formMethods;

  const liveTemplates = useWatch({
    control,
    name: 'carriedInterestFeeTemplates',
  });

  const { defaultTemplate } = useCarriedInterestFeeTemplatesOptions();

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'carriedInterestFeeTemplates',
  });

  const handleRemoveTemplate = (index: number) => {
    if (fields.length < 2) return;

    remove(index);
  };

  const { setSaveButtonPending } = useCarrySettingsContext();
  const options = useHandleTrpcMutation()({
    successMessage: 'Carried interest templates settings updated.',
    onMutate: () => setSaveButtonPending(true),
    onSettled: () => setSaveButtonPending(false),
  });

  const { mutate: updateSettings } =
    api.vehicle.updateCarriedInterestFeeTemplatesSettings.useMutation(options);

  const onSubmit = (data: FormValues) => {
    updateSettings({
      vehicleId: vehicle._id,
      ...data,
    });
  };

  return (
    <FormProvider {...formMethods}>
      <form
        id="carry-settings-form-templates"
        onSubmit={handleSubmit(onSubmit)}
      >
        <PageSection
          withoutBottomDivider
          spacing="lg"
          title={
            <Row centered spacing="sm" justify="space-between" breakpoint="md">
              <Column spacing="xs">
                <Typography variant="inherit">
                  Carried interest templates
                </Typography>
                <Typography variant="body2">
                  Create and manage the templates that define how carried
                  interest is calculated.
                </Typography>
              </Column>
              <Row>
                <Button
                  size="large"
                  variant="contained"
                  type="button"
                  onClick={() =>
                    append({
                      ...defaultTemplate,
                      isDefault: fields.length === 0,
                    })
                  }
                >
                  Add new fee template
                </Button>
              </Row>
            </Row>
          }
        >
          {fields.map((field, index) => (
            <Accordion key={field.id} defaultExpanded>
              <AccordionSummary>
                <Row centered fullWidth justify="space-between" spacing="sm">
                  <Row spacing="sm">
                    <StepIndex
                      index={index + 1}
                      label={
                        liveTemplates?.[index]?.name ||
                        `Carried interest template ${index + 1}`
                      }
                    />
                  </Row>
                  {liveTemplates?.[index]?.isDefault && (
                    <Chip
                      size="small"
                      variant="filled"
                      label="Default template"
                    />
                  )}
                </Row>
              </AccordionSummary>
              <AccordionDetails>
                <Column spacing="lg">
                  <CarriedInterestFeeTemplateComponent
                    index={index}
                    control={control}
                  />
                  <Row>
                    <Button
                      size="large"
                      variant="outlined"
                      onClick={() => handleRemoveTemplate(index)}
                      disabled={
                        fields.length < 2 ||
                        (!!field._id && templateIdsInUse.includes(field._id))
                      }
                    >
                      Remove fee template
                    </Button>
                  </Row>
                </Column>
              </AccordionDetails>
            </Accordion>
          ))}
        </PageSection>
      </form>
    </FormProvider>
  );
};

export default CarriedInterestFeeTemplates;
